import { useEffect, useRef } from 'react';
import { RBAC_ENDPOINTS } from '../config/api';
import { rbacFetch } from '../utils/apiClient';

/**
 * useModuleTracker
 * Measures how long a user spends in a module and reports it for the
 * Login & Engagement Report.
 *
 * Three things make the numbers trustworthy:
 *
 *   Reported in pieces, grouped by visit id.
 *     Time is reported on every module switch, tab switch and page exit, so a
 *     closed window never loses the session. The pieces of one continuous visit
 *     carry the same visit id, which is what lets the report show one long visit
 *     instead of six short ones. A new id begins only on a genuinely new visit.
 *
 *   The last piece survives the page closing.
 *     A plain request is cancelled when the page goes away, which is exactly the
 *     moment the final piece is sent. `keepalive` lets it outlive the page.
 *     (sendBeacon would also survive but cannot carry an Authorization header,
 *     which this API needs.)
 *
 *   The clock stops when the user does.
 *     Without this, someone who reads for five minutes and walks away is
 *     recorded as having stayed until the idle logout fires. Time only counts
 *     while there is mouse, keyboard, scroll or touch activity.
 *
 * @param {string} activeModule - Current active module ('personas' | 'benchmarking')
 */

// No activity for this long and the clock stops until the user returns.
const IDLE_AFTER_MS = 5 * 60 * 1000;

// Anything shorter is noise rather than a visit.
const MIN_REPORTABLE_SECONDS = 5;

/** A visit id. crypto.randomUUID needs a secure context, so fall back if absent. */
function newVisitId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function useModuleTracker(activeModule) {
  const startTimeRef    = useRef(Date.now());
  const lastModuleRef   = useRef(activeModule);
  const visitIdRef      = useRef(newVisitId());
  const lastActivityRef = useRef(Date.now());

  // Seconds genuinely spent since the last report, excluding any stretch where
  // the user had gone quiet. Advancing the start marker as we go means a report
  // never double-counts time already sent.
  const takeElapsedSeconds = () => {
    const now = Date.now();
    const idleFor = now - lastActivityRef.current;
    const until = idleFor > IDLE_AFTER_MS ? lastActivityRef.current : now;
    const seconds = Math.floor((until - startTimeRef.current) / 1000);
    startTimeRef.current = now;
    return Math.max(0, seconds);
  };

  // Report a stretch of time. Fire and forget — tracking must never be able to
  // break the page or hold up navigation.
  const report = (moduleName, durationSeconds, visitId) => {
    if (!moduleName || durationSeconds < MIN_REPORTABLE_SECONDS) return;
    rbacFetch(RBAC_ENDPOINTS.moduleView, {
      method: 'POST',
      keepalive: true, // survives the page being closed
      body: JSON.stringify({
        module_name: moduleName,
        duration_seconds: durationSeconds,
        visit_id: visitId,
      }),
    }).catch(() => {});
  };

  // Treat any real interaction as "still here"
  useEffect(() => {
    const markActive = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, markActive, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, markActive));
  }, []);

  // Switching module ends the visit to the old one and starts a fresh visit
  useEffect(() => {
    if (lastModuleRef.current !== activeModule) {
      report(lastModuleRef.current, takeElapsedSeconds(), visitIdRef.current);
      lastModuleRef.current = activeModule;
      visitIdRef.current = newVisitId();
      lastActivityRef.current = Date.now();
    }
  }, [activeModule]); // eslint-disable-line

  // Leaving the page (navigation away, logout) ends the visit
  useEffect(() => {
    return () => {
      report(lastModuleRef.current, takeElapsedSeconds(), visitIdRef.current);
    };
  }, []); // eslint-disable-line

  // Hiding the tab reports what has been spent so far but keeps the same visit
  // id — coming back to the same module is a continuation, not a new visit.
  useEffect(() => {
    const flush = () => report(lastModuleRef.current, takeElapsedSeconds(), visitIdRef.current);

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flush();
      } else {
        // Back on screen: they are here again, so restart the idle window
        lastActivityRef.current = Date.now();
        startTimeRef.current = Date.now();
      }
    };

    // pagehide covers closing the tab and navigating away, including the cases
    // where visibilitychange is not dispatched. It always reports — checking
    // visibilityState here would skip the report in browsers that still say
    // "visible" while the page is being torn down.
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', flush);
    };
  }, []); // eslint-disable-line
}
